import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '../../api/authorization.provider';
import {
  User,
  useGetTenantQuery,
  useListTeamsQuery,
} from '../../api/graphql/generated';
import { useCurrentUser } from '../../api/user.hooks';
import i18n from '../../i18n';
import { useBeaconRouter } from '../../route/route.hooks';

export interface OnboardingChecklistItem {
  title: string;
  subtitle?: string;
  path: string;
  complete: boolean;
  className: string;
}

const useOnboardingChecklist = (): OnboardingChecklistItem[] => {
  const { i18n } = useTranslation();
  const user = useCurrentUser();

  const tenant = useGetTenantQuery({
    fetchPolicy: 'cache-only',
  });

  const teams = useListTeamsQuery({
    variables: { input: { limit: 1, active: true } },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-only',
  });

  const isAdmin = useHasPermission('admin', 'tenant');

  const { builder } = useBeaconRouter();

  const checklist: OnboardingChecklistItem[] = useMemo(() => {
    if (tenant.data && teams.data) {
      const items: OnboardingChecklistItem[] = [];

      if (isAdmin) {
        items.push({
          title: i18n.t('common:setup_organization'),
          subtitle: i18n.t('common:setup_organization_desc'),
          complete: typeof tenant.data?.getTenant.name === 'string',
          path: builder.getAdminTenantRoute(),
          className: 'onboarding-organization-setup',
        });
      }

      items.push({
        title: i18n.t('common:setup_profile'),
        subtitle: getUserProfileSubtitle(user),
        complete: getUserProfileCompleteStatus(user),
        path: builder.getUserRoute('me'),
        className: 'onboarding-user-setup',
      });

      if (isAdmin) {
        items.push({
          title: i18n.t('common:setup_first_team'),
          subtitle: i18n.t('common:setup_first_team_desc'),
          complete: (teams.data?.listTeams.total ?? 0) > 0,
          path: builder.getNewTeamRoute(),
          className: 'onboarding-team-setup',
        });
      }

      return [
        ...items.filter((c) => !c.complete),
        ...items.filter((c) => c.complete),
      ];
    }

    return [];
  }, [user, tenant, teams, isAdmin, builder, i18n]);

  return checklist;
};

const useOnboardingChecklistRemaining = (): number => {
  const checklist = useOnboardingChecklist();
  let remaining = 0;
  for (const item in checklist) {
    if (checklist[item].complete === false) {
      remaining++;
    }
  }

  return remaining;
};

const getUserProfileSubtitle = (user?: User): string => {
  const isComplete = getUserProfileCompleteStatus(user);
  if (isComplete) {
    return i18n.t('common:setup_profile_desc');
  } else {
    return getUserProfileRemainingParts(user).join(', ');
  }
};

const getUserProfileCompleteStatus = (user?: User): boolean => {
  return getUserProfileRemainingParts(user).length === 0;
};

const getUserProfileRemainingParts = (user?: User): string[] => {
  const parts: string[] = [];

  if (!user?.firstName) {
    parts.push(i18n.t('common:first_name'));
  }

  if (!user?.lastName) {
    parts.push(i18n.t('common:last_name'));
  }

  return parts;
};

export { useOnboardingChecklist, useOnboardingChecklistRemaining };
